import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PERIOD_DEFAULTS = {
  semanal:   7,
  quincenal: 15,
  mensual:   30,
};

function addDays(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function OpsRRHHNominaNuevo() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [legalEntities, setLegalEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    legal_entity_id: "",
    period_type: "quincenal",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    payment_date: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/ops/legal-entities").then(r => setLegalEntities(Array.isArray(r.data) ? r.data : r.data?.entities || [])).catch(() => {});
  }, []);

  // Auto-fill end_date when start_date or period_type changes
  useEffect(() => {
    if (!form.start_date) return;
    const days = PERIOD_DEFAULTS[form.period_type] - 1;
    const end = addDays(form.start_date, days);
    const payment = addDays(end, 1);
    setForm(f => ({ ...f, end_date: end, payment_date: payment }));
  }, [form.start_date, form.period_type]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.legal_entity_id) { toast.error("Selecciona una razón social"); return; }
    if (!form.start_date || !form.end_date || !form.payment_date) { toast.error("Completa todas las fechas"); return; }
    setLoading(true);
    try {
      const r = await api.post("/ops/payroll/periods", form);
      toast.success(`Periodo ${r.data.period_number} creado`);
      navigate(`/ops/rrhh/nomina/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear el periodo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/ops/rrhh/nomina")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-[#FFDF00]" />
            Nuevo periodo de nomina
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Configura el periodo y las fechas de pago</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5 shadow-sm">
        {/* Razon social */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Razon social *</label>
          <select
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
            value={form.legal_entity_id}
            onChange={e => set("legal_entity_id", e.target.value)}
            required
            data-testid="nuevo-entity-select"
          >
            <option value="">Seleccionar razon social...</option>
            {legalEntities.map(e => (
              <option key={e.id} value={e.id}>{e.legal_name} ({e.rfc})</option>
            ))}
          </select>
        </div>

        {/* Tipo de periodo */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tipo de periodo *</label>
          <div className="flex gap-3">
            {["semanal", "quincenal", "mensual"].map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="period_type"
                  value={t}
                  checked={form.period_type === t}
                  onChange={() => set("period_type", t)}
                  className="accent-yellow-400"
                  data-testid={`tipo-${t}`}
                />
                <span className="text-sm capitalize">{t}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Fecha inicio *</label>
            <Input
              type="date"
              value={form.start_date}
              onChange={e => set("start_date", e.target.value)}
              required
              data-testid="nuevo-start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Fecha fin *</label>
            <Input
              type="date"
              value={form.end_date}
              onChange={e => set("end_date", e.target.value)}
              required
              data-testid="nuevo-end-date"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Fecha de pago *</label>
            <Input
              type="date"
              value={form.payment_date}
              onChange={e => set("payment_date", e.target.value)}
              required
              data-testid="nuevo-payment-date"
            />
          </div>
        </div>

        {/* Notas */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Notas (opcional)</label>
          <textarea
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 resize-none"
            rows={2}
            placeholder="Observaciones del periodo..."
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => navigate("/ops/rrhh/nomina")}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#FFDF00] text-slate-900 hover:bg-yellow-400 font-semibold"
            data-testid="nuevo-submit-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {loading ? "Creando..." : "Crear periodo"}
          </Button>
        </div>
      </form>
    </div>
  );
}
